/**
 * ChangeOrderPage (WU-14) — the tokenized change order experience at
 * /p/c/<token>, mirroring ProposalPage:
 *
 *   view → review & respond → ESIGN consent → sign (typed signature + intent
 *   checkbox, hash-bound to the document shown) → confirmation
 *   — or decline with an optional reason.
 *
 * The document is the frozen snapshot verbatim; consent text comes from the
 * view and is recorded server-side (consent endpoint) before accept is ever
 * possible in this UI. A 409 means someone already answered: the view is
 * refreshed and the client is told so plainly.
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  PublicChangeOrderAcceptRequestSchema,
  PublicChangeOrderDeclineRequestSchema,
} from '@buildledger/shared';
import type { PublicChangeOrderView } from '@buildledger/shared';

import { ApiError } from '../api';
import {
  getChangeOrder,
  postChangeOrderAccept,
  postChangeOrderConsent,
  postChangeOrderDecline,
} from '../change-order-api';
import { ChangeOrderDocumentView } from '../ChangeOrderDocumentView';
import { fmtDate, fmtMoney } from '../format';
import { Markdown } from '../markdown';

type LoadState =
  | { kind: 'loading' }
  | { kind: 'error'; status: number; message: string }
  | { kind: 'ready'; data: PublicChangeOrderView };

type Modal = 'consent' | 'sign' | 'decline' | null;

const ALREADY_ANSWERED = 'This change order was already answered.';

export function ChangeOrderPage({ token }: { token: string }): JSX.Element {
  const [load, setLoad] = useState<LoadState>({ kind: 'loading' });
  const [modal, setModal] = useState<Modal>(null);
  const [consentRecorded, setConsentRecorded] = useState(false);
  const [justResponded, setJustResponded] = useState<'accepted' | 'declined' | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  const refresh = useCallback(async () => {
    try {
      const data = await getChangeOrder(token);
      setLoad({ kind: 'ready', data });
    } catch (err) {
      if (err instanceof ApiError) setLoad({ kind: 'error', status: err.status, message: err.message });
      else setLoad({ kind: 'error', status: 0, message: 'Something went wrong loading this change order.' });
    }
  }, [token]);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  const onStateConflict = useCallback(
    (status: number) => {
      setModal(null);
      if (status === 409) setNotice(ALREADY_ANSWERED);
      void refresh();
    },
    [refresh],
  );

  if (load.kind === 'loading') {
    return (
      <div className="stage stage-center">
        <div className="spinner" aria-label="Loading change order" />
      </div>
    );
  }

  if (load.kind === 'error') {
    return <ErrorScreen status={load.status} message={load.message} />;
  }

  const view = load.data;
  const { document } = view;
  const responded = view.response !== null;
  const canRespond = view.canRespond && justResponded === null;
  const consentDone = consentRecorded || view.consentGivenAt !== null;

  return (
    <div className="stage">
      {notice === null ? null : (
        <div className="banner banner-warn" role="status">
          <strong>{notice}</strong>
        </div>
      )}
      <TopBanner view={view} justResponded={justResponded} />

      <main className="doc-wrap">
        <ChangeOrderDocumentView document={document} />
      </main>

      {canRespond ? (
        <div className="action-bar" role="toolbar" aria-label="Respond to this change order">
          <div className="action-bar-inner">
            <div className="action-meta">
              <span className="action-total num">{fmtMoney(document.changeOrder.total)}</span>
              <span className="action-hint">{`Link valid through ${fmtDate(view.expiresAt)}`}</span>
            </div>
            <div className="action-buttons">
              <button className="btn btn-ghost btn-danger" onClick={() => setModal('decline')}>
                Decline
              </button>
              <button
                className="btn btn-primary"
                data-testid="accept-open"
                onClick={() => setModal(consentDone ? 'sign' : 'consent')}
              >
                Accept &amp; sign
              </button>
            </div>
          </div>
        </div>
      ) : (
        <div className="action-bar action-bar-quiet">
          <div className="action-bar-inner">
            <div className="action-meta">
              <span className="action-hint">
                {responded || justResponded
                  ? 'This change order has been responded to.'
                  : view.status === 'void'
                    ? 'This change order was withdrawn by the contractor.'
                    : 'This change order is view-only.'}
              </span>
            </div>
          </div>
        </div>
      )}

      {modal === 'consent' && (
        <ConsentModal
          consentText={view.consentText}
          token={token}
          onDone={() => {
            setConsentRecorded(true);
            setModal('sign');
          }}
          onClose={() => setModal(null)}
          onStateConflict={onStateConflict}
        />
      )}
      {modal === 'sign' && (
        <SignModal
          token={token}
          documentHash={view.contentHash}
          total={document.changeOrder.total}
          revisedTotal={document.contract.revisedTotalIfAccepted}
          issuedAt={document.issuedAt}
          onSigned={() => {
            setModal(null);
            setJustResponded('accepted');
            void refresh();
          }}
          onClose={() => setModal(null)}
          onStateConflict={onStateConflict}
        />
      )}
      {modal === 'decline' && (
        <DeclineModal
          token={token}
          onDeclined={() => {
            setModal(null);
            setJustResponded('declined');
            void refresh();
          }}
          onClose={() => setModal(null)}
          onStateConflict={onStateConflict}
        />
      )}
    </div>
  );
}

// ------------------------------------------------------------------ banners

function TopBanner({
  view,
  justResponded,
}: {
  view: PublicChangeOrderView;
  justResponded: 'accepted' | 'declined' | null;
}): JSX.Element | null {
  const response = view.response;
  const kind = response?.kind ?? justResponded ?? null;
  if (kind === 'accepted') {
    return (
      <div className="banner banner-ok" data-testid="banner-accepted">
        <strong>Accepted &amp; signed.</strong>{' '}
        {response?.signerName && response.signedAt
          ? `Signed by ${response.signerName} on ${fmtDate(response.signedAt)}.`
          : 'Thank you — your signature has been recorded.'}{' '}
        This change order:{' '}
        <span className="num">{fmtMoney(view.document.changeOrder.total)}</span>. Revised contract
        total shown on this change order (as of {fmtDate(view.document.issuedAt)}):{' '}
        <span className="num">{fmtMoney(view.document.contract.revisedTotalIfAccepted)}</span>, based
        on changes approved when it was issued.
      </div>
    );
  }
  if (kind === 'declined') {
    return (
      <div className="banner" data-testid="banner-declined">
        <strong>Declined.</strong>{' '}
        {response?.signerName ? `Declined by ${response.signerName}. ` : ''}
        Your response was sent to the contractor. If anything changes, just reach out to them
        directly.
        {response?.declineReason ? (
          <>
            {' '}
            Reason given: <em>{response.declineReason}</em>
          </>
        ) : null}
      </div>
    );
  }
  if (view.status === 'void') {
    return (
      <div className="banner banner-warn">
        <strong>This change order was withdrawn.</strong> Contact the contractor if you have
        questions.
      </div>
    );
  }
  return null;
}

function ErrorScreen({ status, message }: { status: number; message: string }): JSX.Element {
  const title =
    status === 404
      ? 'This change order link is not valid'
      : status === 410
        ? 'This change order link has expired'
        : status === 429
          ? 'Slow down a moment'
          : 'Something went wrong';
  const hint =
    status === 404
      ? 'Double-check the link from your email — it may have been revoked or replaced.'
      : status === 410
        ? 'Ask the contractor to send a fresh link.'
        : status === 429
          ? 'Too many requests from your connection. Wait a minute and reload.'
          : message;
  return (
    <div className="stage stage-center">
      <div className="card notice-card">
        <div className="brand-rule" />
        <h1>{title}</h1>
        <p>{hint}</p>
      </div>
    </div>
  );
}

// -------------------------------------------------------------------- modal

function ModalShell({
  title,
  children,
  onClose,
}: {
  title: string;
  children: React.ReactNode;
  onClose: () => void;
}): JSX.Element {
  useEffect(() => {
    const onKey = (e: KeyboardEvent): void => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);
  return (
    <div className="overlay" role="presentation" onMouseDown={(e) => e.target === e.currentTarget && onClose()}>
      <div className="modal" role="dialog" aria-modal="true" aria-label={title}>
        <div className="modal-head">
          <h2>{title}</h2>
          <button className="modal-x" aria-label="Close" onClick={onClose}>
            ×
          </button>
        </div>
        {children}
      </div>
    </div>
  );
}

function useSubmit(onStateConflict: (status: number) => void): {
  busy: boolean;
  error: string | null;
  run: (fn: () => Promise<void>) => void;
} {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const run = useCallback(
    (fn: () => Promise<void>) => {
      setBusy(true);
      setError(null);
      void fn()
        .catch((err: unknown) => {
          if (err instanceof ApiError && (err.status === 409 || err.status === 410)) {
            onStateConflict(err.status);
            return;
          }
          setError(err instanceof ApiError ? err.message : 'Something went wrong — try again.');
        })
        .finally(() => setBusy(false));
    },
    [onStateConflict],
  );
  return { busy, error, run };
}

// ------------------------------------------------------------------ consent

function ConsentModal({
  consentText,
  token,
  onDone,
  onClose,
  onStateConflict,
}: {
  consentText: string;
  token: string;
  onDone: () => void;
  onClose: () => void;
  onStateConflict: (status: number) => void;
}): JSX.Element {
  const [agree, setAgree] = useState(false);
  const { busy, error, run } = useSubmit(onStateConflict);
  return (
    <ModalShell title="Electronic records & signatures" onClose={onClose}>
      <div className="modal-body">
        <Markdown className="consent-text" text={consentText} />
        <label className="check-row">
          <input
            type="checkbox"
            checked={agree}
            data-testid="consent-agree"
            onChange={(e) => setAgree(e.target.checked)}
          />
          <span>I agree to use electronic records and signatures for this change order.</span>
        </label>
        {error && <p className="form-error">{error}</p>}
      </div>
      <div className="modal-foot">
        <button className="btn btn-ghost" onClick={onClose} disabled={busy}>
          Cancel
        </button>
        <button
          className="btn btn-primary"
          disabled={!agree || busy}
          data-testid="consent-continue"
          onClick={() =>
            run(async () => {
              await postChangeOrderConsent(token);
              onDone();
            })
          }
        >
          {busy ? 'Saving…' : 'Agree & continue'}
        </button>
      </div>
    </ModalShell>
  );
}

// --------------------------------------------------------------------- sign

function SignModal({
  token,
  documentHash,
  total,
  revisedTotal,
  issuedAt,
  onSigned,
  onClose,
  onStateConflict,
}: {
  token: string;
  documentHash: string;
  total: string;
  revisedTotal: string;
  issuedAt: string;
  onSigned: () => void;
  onClose: () => void;
  onStateConflict: (status: number) => void;
}): JSX.Element {
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [signature, setSignature] = useState('');
  const [intent, setIntent] = useState(false);
  const { busy, error, run } = useSubmit(onStateConflict);

  const parsed = useMemo(
    () =>
      PublicChangeOrderAcceptRequestSchema.safeParse({
        signerName: name.trim(),
        ...(email.trim() === '' ? {} : { signerEmail: email.trim() }),
        typedSignature: signature.trim(),
        intentConfirmed: intent || undefined,
        documentHash,
      }),
    [name, email, signature, intent, documentHash],
  );

  return (
    <ModalShell title="Sign & accept this change order" onClose={onClose}>
      <div className="modal-body">
        <p className="sign-summary">
          This change order: <strong className="num">{fmtMoney(total)}</strong>. You are accepting
          it at this stated amount. The revised contract total shown on this change order (as of{' '}
          {fmtDate(issuedAt)}) is <span className="num">{fmtMoney(revisedTotal)}</span>, based on
          changes approved when it was issued. Your name, email, and the time of signing are
          recorded with the document.
        </p>
        <label className="field">
          <span>Your full name</span>
          <input
            value={name}
            data-testid="sign-name"
            autoComplete="name"
            onChange={(e) => setName(e.target.value)}
            placeholder="Jordan Smith"
          />
        </label>
        <label className="field">
          <span>Email (optional)</span>
          <input
            value={email}
            data-testid="sign-email"
            type="email"
            autoComplete="email"
            onChange={(e) => setEmail(e.target.value)}
            placeholder="you@example.com"
          />
        </label>
        <label className="field">
          <span>Type your name to sign</span>
          <input
            value={signature}
            data-testid="sign-signature"
            className="signature-input"
            onChange={(e) => setSignature(e.target.value)}
            placeholder={name.trim() === '' ? 'Your signature' : name.trim()}
          />
        </label>
        <label className="check-row">
          <input
            type="checkbox"
            checked={intent}
            data-testid="sign-intent"
            onChange={(e) => setIntent(e.target.checked)}
          />
          <span>
            I intend this typed name to be my electronic signature on this change order, and I agree
            to its terms.
          </span>
        </label>
        {error && <p className="form-error">{error}</p>}
      </div>
      <div className="modal-foot">
        <button className="btn btn-ghost" onClick={onClose} disabled={busy}>
          Back
        </button>
        <button
          className="btn btn-primary"
          disabled={!parsed.success || busy}
          data-testid="sign-submit"
          onClick={() =>
            run(async () => {
              if (!parsed.success) return;
              await postChangeOrderAccept(token, parsed.data);
              onSigned();
            })
          }
        >
          {busy ? 'Signing…' : 'Sign & accept'}
        </button>
      </div>
    </ModalShell>
  );
}

// ------------------------------------------------------------------ decline

function DeclineModal({
  token,
  onDeclined,
  onClose,
  onStateConflict,
}: {
  token: string;
  onDeclined: () => void;
  onClose: () => void;
  onStateConflict: (status: number) => void;
}): JSX.Element {
  const [reason, setReason] = useState('');
  const [name, setName] = useState('');
  const { busy, error, run } = useSubmit(onStateConflict);

  const parsed = useMemo(
    () =>
      PublicChangeOrderDeclineRequestSchema.safeParse({
        ...(reason.trim() === '' ? {} : { reason: reason.trim() }),
        ...(name.trim() === '' ? {} : { signerName: name.trim() }),
      }),
    [reason, name],
  );

  return (
    <ModalShell title="Decline this change order" onClose={onClose}>
      <div className="modal-body">
        <p className="sign-summary">
          Let the contractor know why — it goes straight to them and helps them follow up well.
        </p>
        <label className="field">
          <span>Reason (optional)</span>
          <textarea
            value={reason}
            data-testid="decline-reason"
            rows={4}
            onChange={(e) => setReason(e.target.value)}
            placeholder="e.g. The price is higher than expected / we'd rather skip this change…"
          />
        </label>
        <label className="field">
          <span>Your name (optional)</span>
          <input value={name} onChange={(e) => setName(e.target.value)} autoComplete="name" />
        </label>
        {error && <p className="form-error">{error}</p>}
      </div>
      <div className="modal-foot">
        <button className="btn btn-ghost" onClick={onClose} disabled={busy}>
          Back
        </button>
        <button
          className="btn btn-danger-solid"
          disabled={!parsed.success || busy}
          data-testid="decline-submit"
          onClick={() =>
            run(async () => {
              if (!parsed.success) return;
              await postChangeOrderDecline(token, parsed.data);
              onDeclined();
            })
          }
        >
          {busy ? 'Sending…' : 'Decline change order'}
        </button>
      </div>
    </ModalShell>
  );
}
